import ApiClient from "@/domain/core/utils/apiClient";
import client_core from "@/services/clients/client_core";
import moment from "moment";
import {ValidationError} from "@/domain/core/exception/exceptions";

const client = new ApiClient(client_core)

const endpoints = {
    search: '/dashboard/v2/photo-search/{id}/search/?gallery_id={gallery}',
    newly_created: '/dashboard/v2/photo-search/{id}/newly_created/?gallery_id={gallery}',
    newly_processed: '/dashboard/v2/photo-search/{id}/newly_processed/?gallery_id={gallery}',
    download: '/dashboard/v2/photo-search/{id}/download/?gallery_id={gallery}',
    download_single: '/dashboard/v2/photo-search/{id}/download_single/?gallery_id={gallery}',
    config: '/dashboard/v2/photo-search/{id}/config/?gallery_id={gallery}',
    timeline: '/dashboard/v2/photo-search/{id}/timeline/?gallery_id={gallery}',
    filter: '/dashboard/v2/photo-search/{id}/filter/?gallery_id={gallery}',
    filters: '/dashboard/v2/photo-search/{id}/filters/?gallery_id={gallery}',
    tag_definitions: '/dashboard/v2/photo-archive/{id}/tag-definitions/',
    selected_stats: '/dashboard/v2/photo-search/{id}/selected_stats/?gallery_id={gallery}',
    ids: '/dashboard/v2/photo-search/{id}/ids/?gallery_id={gallery}',
    tags: '/dashboard/v2/photo-search/{id}/tags/?gallery_id={gallery}',
    add_tags: '/dashboard/v2/photo-search/{id}/add_tags/?gallery_id={gallery}',
    remove_tags: '/dashboard/v2/photo-search/{id}/remove_tags/?gallery_id={gallery}',
    add_archive_tags: '/dashboard/v2/photo-search/{id}/add_archive_tags/',
    remove_archive_tags: '/dashboard/v2/photo-search/{id}/remove_archive_tags/',
    media_tags: '/dashboard/v2/photo-search/{id}/media-tags/',
    face_update_tags: '/dashboard/v2/photo-search/{id}/face/{face_id}/update_tags/?gallery_id={gallery}',
    face_delete: '/dashboard/v2/photo-search/{id}/face/{face_id}/delete/?gallery_id={gallery}',
    galleries: '/dashboard/v2/photo-search/{id}/galleries/?gallery_id={gallery}',
    set_gallery: '/dashboard/v2/photo-search/{id}/set_gallery/?gallery_id={gallery}',
    copy: '/dashboard/v2/photo-search/{id}/copy/?gallery_id={gallery}',
    move_top: '/dashboard/v2/photo-search/{id}/move_top/?gallery_id={gallery}',
    move_bottom: '/dashboard/v2/photo-search/{id}/move_bottom/?gallery_id={gallery}',
    reorder: '/dashboard/v2/photo-search/{id}/reorder/?gallery_id={gallery}',
    delete_media: '/dashboard/v2/photo-search/{id}/delete/?gallery_id={gallery}',
    soft_delete: '/dashboard/v2/photo-search/{id}/soft_delete/?gallery_id={gallery}',
    restore: '/dashboard/v2/photo-search/{id}/restore/?gallery_id={gallery}',
    export_media_pica_code: '/dashboard/v2/photo-search/{id}/export_media_pica_code/?gallery_id={gallery}',
    media_detail: '/dashboard/v2/photo-search/{id}/media/{media_id}/detail/?gallery_id={gallery}',
    generate_checkin_from_face: '/dashboard/v2/photo-search/{id}/generate_checkin_from_face/?gallery_id={gallery}',
    set_pica_code: '/dashboard/v2/photo-search/{id}/set_pica_code/?gallery_id={gallery}',
    unlink_pica_code: '/dashboard/v2/photo-search/{id}/unlink_pica_code/?gallery_id={gallery}',
    validate_pica_code: '/dashboard/v2/photo-search/{id}/validate_pica_code/?gallery_id={gallery}&pica_code={pica_code}',
    toggle_photographer_pick: '/dashboard/v2/photo-search/{id}/toggle_photographer_pick/?gallery_id={gallery}',
}

export default {
    async fetchConfig({pica_service_id, gallery_id}, sorting = null) {
        return await client.get(endpoints.config.replace('{id}', pica_service_id).replace('{gallery}', gallery_id),{sorting})
    },
    async fetchTagDefinitions({pica_service_id}) {
        const response = await client.get(endpoints.tag_definitions.replace('{id}', pica_service_id))
        return response.definitions || []
    },
    async fetchTimeline({pica_service_id, gallery_id}, filters, sorting = null) {
        return await client.post(endpoints.timeline.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            filters: mapFilters(filters)
        }, {sorting})
    },
    async fetchPhotos({pica_service_id, gallery_id}, filters, selected, sorting = null) {
        // Extract cursor metadata (date, cursor, include_cursor) and cursor IDs from filters
        const {cursor_before_id, cursor_after_id, date, cursor, include_cursor, ...otherFilters} = filters
        const mappedFilters = mapFilters(otherFilters)

        // Build payload with cursor metadata at root level (not in filters)
        const payload = {
            filters: mappedFilters,
            selected: mapSelectedFilters(selected),
            ...(cursor_before_id && {cursor_before_id}),
            ...(cursor_after_id && {cursor_after_id}),
            ...(date && {date: moment(date).toISOString()}),
            ...(cursor && {cursor}),
            ...(include_cursor && {include_cursor: true})
        }

        return await client.post(endpoints.filter.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), payload, {sorting})
    },
    async fetchFilterValues({pica_service_id, gallery_id}) {
        return await client.get(endpoints.filters.replace('{id}', pica_service_id).replace('{gallery}', gallery_id))
    },
    async fetchSearch({pica_service_id, gallery_id}, query) {
        return await client.post(endpoints.search.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), query)
    },
    async fetchNewMedia({pica_service_id, gallery_id}, created_at, taskId, filters, selected) {
        return await client.post(endpoints.newly_created.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            filters: mapFilters(filters),
            selected: mapSelectedFilters(selected),
            created_at: created_at ? created_at.toISOString() : null,
            upload_task: taskId
        })
    },
    async fetchNewProcessed({pica_service_id, gallery_id}, {processed_at = null, ids, taskId}) {
        let iso_processed_at = null
        if (processed_at)
            iso_processed_at = moment(processed_at).toISOString()

        return await client.post(endpoints.newly_processed.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            processed_at: iso_processed_at,
            visible_ids: ids??[],
            upload_task: taskId
        })
    },
    async downloadPhotos({pica_service_id, gallery_id}, selected, email, appyLogo, single_file = false, page = null, limit = null) {
        const payload = {
            selected: mapSelectedFilters(selected),
            email,
            logo: appyLogo
        }

        if (page !== null) payload.page = page
        if (limit !== null) payload.limit = limit

        const endpoint = single_file ? endpoints.download_single : endpoints.download
        return await client.post(endpoint.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), payload, {}, 'download')
    },
    async downloadPhotosLongTask({pica_service_id, gallery_id}, selected, appyLogo) {
        const payload = {
            selected: mapSelectedFilters(selected),
            logo: appyLogo,
            async_task: true
        }
        return await client.post(endpoints.download.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), payload)
    },
    async fetchIdsForDate({pica_service_id, gallery_id}, start, end, filters, sorting = null) {
        return await client.post(endpoints.ids.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            filters: mapFilters(filters),
            date_start: start.toISOString(),
            date_end: end.toISOString(),
        }, {sorting})
    },
    async fetchTags({pica_service_id, gallery_id}) {
        return await client.get(endpoints.tags.replace('{id}', pica_service_id).replace('{gallery}', gallery_id))
    },
    async addTags({pica_service_id, gallery_id}, selected, tags, filters) {
        return await client.post(endpoints.add_tags.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            tags: tags,
            filters: mapFilters(filters)
        })
    },
    async removeTags({pica_service_id, gallery_id}, selected, filters) {
        return await client.post(endpoints.remove_tags.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            filters: mapFilters(filters)
        })
    },
    async setMediaTags({pica_service_id, gallery_id}, selected, tags, filters) {
        const url = endpoints.add_archive_tags.replace('{id}', pica_service_id)
        const queryParams = gallery_id ? `?gallery_id=${gallery_id}` : ''

        return await client.post(url + queryParams, {
            selected: mapSelectedFilters(selected),
            tags: tags,
            filters: mapFilters(filters)
        })
    },
    async resetMediaTags({pica_service_id, gallery_id}, selected, tag_codes, filters) {
        const url = endpoints.remove_archive_tags.replace('{id}', pica_service_id)
        const queryParams = gallery_id ? `?gallery_id=${gallery_id}` : ''

        return await client.post(url + queryParams, {
            selected: mapSelectedFilters(selected),
            tag_codes: tag_codes,
            filters: mapFilters(filters)
        })
    },
    async updateMediaTags({pica_service_id, gallery_id}, selected, operations, filters) {
        const url = endpoints.media_tags.replace('{id}', pica_service_id)
        const queryParams = gallery_id ? `?gallery_id=${gallery_id}` : ''

        return await client.post(url + queryParams, {
            selected: mapSelectedFilters(selected),
            operations: operations,
            filters: mapFilters(filters)
        })
    },
    async fetchGalleries({pica_service_id, gallery_id}) {
        return await client.get(endpoints.galleries.replace('{id}', pica_service_id).replace('{gallery}', gallery_id))
    },
    async setGallery({pica_service_id, gallery_id}, selected, gallery, filters) {
        return await client.post(endpoints.set_gallery.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            gallery: gallery,
            filters: mapFilters(filters)
        })
    },
    async copyToGallery({pica_service_id, gallery_id}, selected, gallery) {
        return await client.post(endpoints.copy.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            gallery: gallery
        })
    },
    async deleteMedia({pica_service_id, gallery_id}, selected, filters) {
        return await client.post(endpoints.delete_media.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            filters: mapFilters(filters)
        })
    },
    async moveTop({pica_service_id, gallery_id}, selected) {
        return await client.post(endpoints.move_top.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected)
        })
    },
    async moveBottom({pica_service_id, gallery_id}, selected) {
        return await client.post(endpoints.move_bottom.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected)
        })
    },
    async reorder({pica_service_id, gallery_id}, selected, before, after) {
        return await client.post(endpoints.reorder.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            before: before > 0 ? before : null,
            after: after > 0 ? after : null,
        })
    },
    async softDelete({pica_service_id, gallery_id}, selected, filters) {
        return await client.post(endpoints.soft_delete.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            filters: mapFilters(filters)
        })
    },
    async restore({pica_service_id, gallery_id}, selected, filters) {
        return await client.post(endpoints.restore.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            filters: mapFilters(filters)
        })
    },
    async exportMediaPicaCode({pica_service_id, gallery_id}) {
        await client.download(endpoints.export_media_pica_code.replace('{id}', pica_service_id).replace('{gallery}', gallery_id),null,null,'POST')
    },
    async fetchMediaDetail({pica_service_id, gallery_id}, media_id) {
        return await client.get(endpoints.media_detail.replace('{id}', pica_service_id).replace('{gallery}', gallery_id).replace('{media_id}', media_id))
    },
    async generateCheckinFromFace({pica_service_id, gallery_id}, selected) {
        return await client.download(endpoints.generate_checkin_from_face.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), null,{
            selected: mapSelectedFilters(selected)
        },  'POST')
    },
    async setPicaCode({pica_service_id, gallery_id}, selected, pica_code, filters) {
        return await client.post(endpoints.set_pica_code.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            pica_code: pica_code,
            filters: mapFilters(filters)
        })
    },
    async unlinkPicaCode({pica_service_id, gallery_id}, selected, pica_code, filters) {
        return await client.post(endpoints.unlink_pica_code.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected),
            pica_code: pica_code,
            filters: mapFilters(filters)
        })
    },
    async validatePicaCode({pica_service_id, gallery_id}, pica_code) {
        try {
            await client.get(endpoints.validate_pica_code.replace('{id}', pica_service_id).replace('{gallery}', gallery_id).replace('{pica_code}', pica_code))
            return true
        } catch (e) {
            if (e instanceof ValidationError)
                return false
            else
                throw e
        }

    },
    async updateFaceTags({pica_service_id, gallery_id}, face_id, tags) {
        return await client.post(endpoints.face_update_tags.replace('{id}', pica_service_id).replace('{gallery}', gallery_id).replace('{face_id}', face_id), {
            tags: tags
        })
    },
    async deleteFace({pica_service_id, gallery_id}, face_id) {
        return await client.post(endpoints.face_delete.replace('{id}', pica_service_id).replace('{gallery}', gallery_id).replace('{face_id}', face_id))
    },
    async togglePhotographerPick({pica_service_id, gallery_id}, selected, value, filters) {
        return await client.post(
            endpoints.toggle_photographer_pick.replace('{id}', pica_service_id).replace('{gallery}', gallery_id),
            {
                selected: mapSelectedFilters(selected),
                value: value,
                filters: mapFilters(filters)
            }
        )
    },
    async fetchSelectedStats({pica_service_id, gallery_id}, selected) {
        return await client.post(endpoints.selected_stats.replace('{id}', pica_service_id).replace('{gallery}', gallery_id), {
            selected: mapSelectedFilters(selected)
        })
    },
    async abort() {
       client.abort()
    }
}

/**
 * Map filters to API format (converts date_range to date_start/date_end)
 */
export function mapFilters(filters) {
    return Object.keys(filters).reduce((acc, key) => {
        if (!filters[key] === null) return acc
        if (key === 'date')
            acc.date = moment(filters.date)
        else if (key === 'date_range') {
            if (filters.date_range.start)
                acc.date_start = moment(filters.date_range.start)
            if (filters.date_range.end)
                acc.date_end = moment(filters.date_range.end)
        } else {
            acc[key] = filters[key]
        }
        return acc
    }, {})
}

function mapSelectedFilters(selected) {
    return selected.map(filter => mapFilters(filter))
}
